import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
} from "@tanstack/react-router";
import {
  Bot,
  Lock,
  Plus,
  Settings,
  Trash2,
} from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import {
  areStringArraysEqual,
  buildRepoIssueBotSelectionDrafts,
  isIssueBotReferenced,
  normalizeBotIdsForCatalog,
  toggleCatalogBotSelection,
  type RepoIssueBotSelectionDraft,
  type RepoIssueBotSelectionMode,
} from "@/github-app/issue-creation-bot-state";
import { useOrganizationCanManage } from "@/hooks/organizations/use-organization-can-manage";
import { useTRPC } from "@/infrastructure/trpc/react";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/ui/card";
import { Checkbox } from "@/shared/ui/checkbox";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";

export const Route = createFileRoute(
  "/app/organization/$id/settings/github/bots"
)({
  loader: async ({ context, params }) => {
    const installation = await context.queryClient.query(
      context.trpc.githubApp.getInstallation.queryOptions({
        organizationId: params.id,
      })
    );

    if (installation) {
      await Promise.all([
        context.queryClient
          .query(
            context.trpc.githubApp.listRepositories.queryOptions(
              {
                organizationId: params.id,
              }
            )
          )
          .catch(() => undefined),
        context.queryClient
          .query(
            context.trpc.githubApp.getRepoAssignees.queryOptions(
              {
                organizationId: params.id,
              }
            )
          )
          .catch(() => undefined),
        context.queryClient
          .query(
            context.trpc.githubApp.listIssueBots.queryOptions(
              {
                organizationId: params.id,
              }
            )
          )
          .catch(() => undefined),
      ]);
    }
  },
  component: RouteComponent,
});

const BOT_SELECTION_MODE_OPTIONS = [
  {
    label: "Inherit installation defaults",
    value: "inherit" as const,
  },
  {
    label: "Custom repository selection",
    value: "custom" as const,
  },
];

function RouteComponent() {
  const { id } = Route.useParams();
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { canManage } = useOrganizationCanManage(id);

  const { data: installation } = useSuspenseQuery(
    trpc.githubApp.getInstallation.queryOptions({
      organizationId: id,
    })
  );

  const { data: repositories } = useQuery({
    ...trpc.githubApp.listRepositories.queryOptions({
      organizationId: id,
    }),
    enabled: !!installation,
  });

  const { data: repoAssignees } = useQuery({
    ...trpc.githubApp.getRepoAssignees.queryOptions({
      organizationId: id,
    }),
    enabled: !!installation,
  });

  const { data: issueBots } = useQuery({
    ...trpc.githubApp.listIssueBots.queryOptions({
      organizationId: id,
    }),
    enabled: !!installation,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const [defaultBotIds, setDefaultBotIds] = useState<
    string[]
  >([]);
  const [newBotHandle, setNewBotHandle] = useState("");
  const [
    pendingRepoBotSelectionRepo,
    setPendingRepoBotSelectionRepo,
  ] = useState<string | null>(null);
  const [
    repoBotSelectionDrafts,
    setRepoBotSelectionDrafts,
  ] = useState<Record<string, RepoIssueBotSelectionDraft>>(
    {}
  );
  const [syncedInputs, setSyncedInputs] = useState<{
    settingsKey: string | null;
    repoKey: string | null;
    catalog: typeof issueBots;
  }>({
    settingsKey: null,
    repoKey: null,
    catalog: issueBots,
  });

  const catalogIssueBots = issueBots ?? [];
  const sortedRepos = useMemo(
    () =>
      [...(repositories ?? [])].sort((left, right) =>
        left.fullName.localeCompare(right.fullName)
      ),
    [repositories]
  );
  const defaultBotIdsFromServer = useMemo(
    () =>
      installation
        ? normalizeBotIdsForCatalog(
            installation.defaultBotIds,
            catalogIssueBots
          )
        : [],
    [catalogIssueBots, installation]
  );
  const repoBotSelectionsFromServer = useMemo(
    () =>
      buildRepoIssueBotSelectionDrafts(
        sortedRepos,
        repoAssignees ?? [],
        catalogIssueBots
      ),
    [sortedRepos, repoAssignees, catalogIssueBots]
  );
  const issueBotHandleById = useMemo(
    () =>
      new Map(
        catalogIssueBots.map((issueBot) => [
          issueBot.id,
          issueBot.handle,
        ])
      ),
    [catalogIssueBots]
  );
  const installationDefaultBotIdsSyncKey = useMemo(
    () =>
      installation
        ? JSON.stringify({
            defaultBotIds: installation.defaultBotIds,
          })
        : null,
    [installation]
  );
  const repoBotSelectionsSyncKey = useMemo(
    () =>
      repositories === undefined ||
      repoAssignees === undefined
        ? null
        : JSON.stringify({
            repositories: sortedRepos.map(
              (repo) => repo.fullName
            ),
            repoSelections: [...repoAssignees]
              .sort((left, right) =>
                left.repositoryFullName.localeCompare(
                  right.repositoryFullName
                )
              )
              .map((repoAssignee) => ({
                repositoryFullName:
                  repoAssignee.repositoryFullName,
                assignees: repoAssignee.assignees,
                botSelectionMode:
                  repoAssignee.botSelectionMode,
                selectedBotIds: repoAssignee.selectedBotIds,
              })),
          }),
    [repoAssignees, repositories, sortedRepos]
  );

  const settingsKey =
    issueBots === undefined
      ? null
      : installationDefaultBotIdsSyncKey;
  const repoKey =
    issueBots === undefined
      ? null
      : repoBotSelectionsSyncKey;
  if (
    syncedInputs.settingsKey !== settingsKey ||
    syncedInputs.repoKey !== repoKey ||
    syncedInputs.catalog !== issueBots
  ) {
    setSyncedInputs({
      settingsKey,
      repoKey,
      catalog: issueBots,
    });
    if (
      issueBots !== undefined &&
      settingsKey !== null &&
      syncedInputs.settingsKey !== settingsKey
    ) {
      setDefaultBotIds(defaultBotIdsFromServer);
    } else if (
      issueBots !== undefined &&
      syncedInputs.catalog !== issueBots
    ) {
      setDefaultBotIds((currentBotIds) => {
        const normalizedBotIds = normalizeBotIdsForCatalog(
          currentBotIds,
          catalogIssueBots
        );

        return areStringArraysEqual(
          currentBotIds,
          normalizedBotIds
        )
          ? currentBotIds
          : normalizedBotIds;
      });
    }
    if (
      issueBots !== undefined &&
      repoKey !== null &&
      syncedInputs.repoKey !== repoKey
    ) {
      setRepoBotSelectionDrafts({});
    } else if (
      issueBots !== undefined &&
      syncedInputs.catalog !== issueBots
    ) {
      setRepoBotSelectionDrafts((currentDrafts) => {
        let hasNormalizedDraft = false;
        const normalizedDrafts: Record<
          string,
          RepoIssueBotSelectionDraft
        > = {};

        for (const [
          repositoryFullName,
          draft,
        ] of Object.entries(currentDrafts)) {
          if (draft.botSelectionMode !== "custom") {
            normalizedDrafts[repositoryFullName] = draft;
            continue;
          }

          const normalizedSelectedBotIds =
            normalizeBotIdsForCatalog(
              draft.selectedBotIds,
              catalogIssueBots
            );

          if (
            areStringArraysEqual(
              draft.selectedBotIds,
              normalizedSelectedBotIds
            )
          ) {
            normalizedDrafts[repositoryFullName] = draft;
            continue;
          }

          hasNormalizedDraft = true;
          normalizedDrafts[repositoryFullName] = {
            ...draft,
            selectedBotIds: normalizedSelectedBotIds,
          };
        }

        return hasNormalizedDraft
          ? normalizedDrafts
          : currentDrafts;
      });
    }
  }

  const updateSettings = useMutation(
    trpc.githubApp.updateSettings.mutationOptions({
      onSuccess: async () => {
        toast.success("Installation defaults updated");
        await queryClient.invalidateQueries({
          queryKey:
            trpc.githubApp.getInstallation.queryKey(),
        });
      },
      onError: (error) => {
        toast.error(
          error.message ||
            "Failed to update installation default bot mentions"
        );
        void queryClient.invalidateQueries({
          queryKey:
            trpc.githubApp.getInstallation.queryKey(),
        });
      },
    })
  );

  const createIssueBot = useMutation(
    trpc.githubApp.createIssueBot.mutationOptions({
      onSuccess: async () => {
        toast.success("Bot added to catalog");
        setNewBotHandle("");
        await queryClient.invalidateQueries({
          queryKey: trpc.githubApp.listIssueBots.queryKey(),
        });
      },
      onError: (error) => {
        toast.error(
          error.message || "Failed to create issue bot"
        );
      },
    })
  );

  const deleteIssueBot = useMutation(
    trpc.githubApp.deleteIssueBot.mutationOptions({
      onSuccess: async () => {
        toast.success("Bot removed from catalog");
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey:
              trpc.githubApp.listIssueBots.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.githubApp.getInstallation.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.githubApp.getRepoAssignees.queryKey(),
          }),
        ]);
      },
      onError: (error) => {
        toast.error(
          error.message || "Failed to delete issue bot"
        );
      },
    })
  );

  const updateRepoAssignees = useMutation(
    trpc.githubApp.updateRepoAssignees.mutationOptions()
  );

  const getRepoAssignees = (repoFullName: string) => {
    const found = repoAssignees?.find(
      (repoAssignee) =>
        repoAssignee.repositoryFullName === repoFullName
    );

    return found?.assignees ?? [];
  };

  const repoBotSelectionEntries = sortedRepos.map(
    (repo) => {
      const selection = repoBotSelectionDrafts[
        repo.fullName
      ] ??
        repoBotSelectionsFromServer[repo.fullName] ?? {
          assignees: [],
          botSelectionMode: "inherit",
          selectedBotIds: [],
        };

      return {
        repo,
        selection,
      };
    }
  );

  const currentRepoBotSelections = useMemo(
    () =>
      Object.fromEntries(
        repoBotSelectionEntries.map(
          ({ repo, selection }) => [
            repo.fullName,
            selection,
          ]
        )
      ) satisfies Record<
        string,
        RepoIssueBotSelectionDraft
      >,
    [repoBotSelectionEntries]
  );

  const defaultBotHandlesSummary = defaultBotIds
    .map((botId) => issueBotHandleById.get(botId))
    .filter((handle): handle is string => Boolean(handle));

  const updateRepoBotDraft = (
    repositoryFullName: string,
    updater: (
      currentDraft: RepoIssueBotSelectionDraft
    ) => RepoIssueBotSelectionDraft
  ) => {
    const fallbackDraft = repoBotSelectionsFromServer[
      repositoryFullName
    ] ?? {
      assignees: getRepoAssignees(repositoryFullName),
      botSelectionMode: "inherit",
      selectedBotIds: [],
    };

    setRepoBotSelectionDrafts((currentDrafts) => ({
      ...currentDrafts,
      [repositoryFullName]: updater(
        currentDrafts[repositoryFullName] ?? fallbackDraft
      ),
    }));
  };

  const hasPendingRepoBotSelection =
    pendingRepoBotSelectionRepo !== null;

  const isRepoBotSelectionPending = (
    repositoryFullName: string
  ) => pendingRepoBotSelectionRepo === repositoryFullName;

  const handleToggleDefaultBotMention = (botId: string) => {
    if (
      !installation ||
      !canManage ||
      updateSettings.isPending
    ) {
      return;
    }

    const nextDefaultBotIds = toggleCatalogBotSelection(
      defaultBotIds,
      botId,
      catalogIssueBots
    );

    setDefaultBotIds(nextDefaultBotIds);
    updateSettings.mutate({
      organizationId: id,
      defaultBotIds: nextDefaultBotIds,
    });
  };

  const handleCreateIssueBot = () => {
    if (!newBotHandle.trim()) {
      return;
    }

    createIssueBot.mutate({
      organizationId: id,
      handle: newBotHandle,
    });
  };

  const handleSaveRepoBotSelection = async ({
    repositoryFullName,
    nextSelection,
    previousSelection,
  }: {
    repositoryFullName: string;
    nextSelection: RepoIssueBotSelectionDraft;
    previousSelection: RepoIssueBotSelectionDraft;
  }) => {
    if (!canManage || hasPendingRepoBotSelection) {
      return;
    }

    const normalizedNextSelection = {
      ...nextSelection,
      selectedBotIds:
        nextSelection.botSelectionMode === "custom"
          ? normalizeBotIdsForCatalog(
              nextSelection.selectedBotIds,
              catalogIssueBots
            )
          : [],
    } satisfies RepoIssueBotSelectionDraft;

    updateRepoBotDraft(
      repositoryFullName,
      () => normalizedNextSelection
    );
    setPendingRepoBotSelectionRepo(repositoryFullName);

    try {
      await updateRepoAssignees.mutateAsync({
        organizationId: id,
        repositoryFullName,
        assignees: normalizedNextSelection.assignees,
        botSelectionMode:
          normalizedNextSelection.botSelectionMode,
        selectedBotIds:
          normalizedNextSelection.selectedBotIds,
      });

      toast.success("Repository bot mentions updated");
      await queryClient.invalidateQueries({
        queryKey:
          trpc.githubApp.getRepoAssignees.queryKey(),
      });
    } catch (error) {
      updateRepoBotDraft(
        repositoryFullName,
        () => previousSelection
      );
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to update repository bot mentions"
      );
      void queryClient.invalidateQueries({
        queryKey:
          trpc.githubApp.getRepoAssignees.queryKey(),
      });
    } finally {
      setPendingRepoBotSelectionRepo(null);
    }
  };

  if (!installation) {
    return (
      <div className="space-y-6">
        <div>
          <h3 className="mb-1 text-lg font-semibold">
            GitHub Bots
          </h3>
          <p className="text-sm text-muted-foreground">
            Manage the shared bot catalog and repository bot
            mention rules.
          </p>
        </div>

        <Card>
          <CardContent
            variant="py12"
            className="text-center"
          >
            <Bot className="mx-auto mb-4 h-12 w-12 text-muted-foreground/30" />
            <h3 className="mb-2 font-medium">
              GitHub App Not Connected
            </h3>
            <p className="mb-4 text-sm text-muted-foreground">
              Connect the GitHub App first to configure bot
              mentions.
            </p>
            <Link
              to="/app/organization/$id/settings/github"
              params={{ id }}
            >
              <Button>Connect GitHub App</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-1 text-lg font-semibold">
          GitHub Bots
        </h3>
        <p className="text-sm text-muted-foreground">
          Manage the installation defaults, reusable bot
          catalog, and repository-specific bot mentions.
        </p>
      </div>

      <Card>
        <CardHeader variant="compact">
          <div className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            <CardTitle variant="base">
              Installation Default Bot Mentions
            </CardTitle>
          </div>
          <CardDescription>
            These bot mentions apply to repositories that
            inherit the installation defaults.
          </CardDescription>
        </CardHeader>
        <CardContent variant="space4">
          {catalogIssueBots.length > 0 ? (
            <div className="space-y-2 rounded-lg border p-3">
              {catalogIssueBots.map((issueBot) => {
                const inUse = isIssueBotReferenced({
                  botId: issueBot.id,
                  defaultBotIds,
                  repoSelections: currentRepoBotSelections,
                });

                return (
                  <div
                    key={issueBot.id}
                    className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <Checkbox
                        checked={defaultBotIds.includes(
                          issueBot.id
                        )}
                        onCheckedChange={() => {
                          handleToggleDefaultBotMention(
                            issueBot.id
                          );
                        }}
                        disabled={
                          !canManage ||
                          updateSettings.isPending
                        }
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-medium">
                          @{issueBot.handle}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {defaultBotIds.includes(
                            issueBot.id
                          )
                            ? "Selected as an installation default"
                            : "Available in the organization catalog"}
                        </p>
                      </div>
                    </div>
                    {inUse && (
                      <Badge variant="secondary">
                        In use
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed px-4 py-3 text-sm text-muted-foreground">
              Add at least one bot to the catalog below to
              configure installation defaults.
            </div>
          )}

          {canManage && (
            <p className="text-xs text-muted-foreground">
              {updateSettings.isPending
                ? "Saving changes..."
                : "Changes save automatically."}
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader variant="compact">
          <div className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            <CardTitle variant="base">
              Bot Catalog
            </CardTitle>
          </div>
          <CardDescription>
            Create reusable bot handles for installation
            defaults and repository-specific mention rules.
          </CardDescription>
        </CardHeader>
        <CardContent variant="space4">
          {canManage && (
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                value={newBotHandle}
                onChange={(event) => {
                  setNewBotHandle(event.target.value);
                }}
                placeholder="bot-handle"
                className="sm:max-w-xs"
              />
              <Button
                type="button"
                onClick={handleCreateIssueBot}
                disabled={
                  !newBotHandle.trim() ||
                  createIssueBot.isPending
                }
              >
                <Plus className="mr-2 h-4 w-4" />
                {createIssueBot.isPending
                  ? "Adding..."
                  : "Add Bot"}
              </Button>
            </div>
          )}

          {catalogIssueBots.length > 0 ? (
            <div className="divide-y overflow-hidden rounded-lg border">
              {catalogIssueBots.map((issueBot) => {
                const inUse = isIssueBotReferenced({
                  botId: issueBot.id,
                  defaultBotIds,
                  repoSelections: currentRepoBotSelections,
                });

                return (
                  <div
                    key={issueBot.id}
                    className="flex items-center justify-between gap-3 px-4 py-3"
                  >
                    <div className="min-w-0 space-y-1">
                      <p className="text-sm font-medium">
                        @{issueBot.handle}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {inUse
                          ? "Remove this bot from defaults and custom repo selections before deleting it."
                          : "Unused bots can be deleted from the catalog."}
                      </p>
                    </div>

                    <div className="flex flex-shrink-0 items-center gap-2">
                      {inUse && (
                        <Badge variant="secondary">
                          In use
                        </Badge>
                      )}
                      {canManage && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            deleteIssueBot.mutate({
                              organizationId: id,
                              botId: issueBot.id,
                            });
                          }}
                          disabled={
                            inUse ||
                            deleteIssueBot.isPending
                          }
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="rounded-lg border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">
              No bots in the catalog yet.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader variant="compact">
          <div className="flex items-center gap-2">
            <Bot className="h-4 w-4 text-muted-foreground" />
            <CardTitle variant="base">
              Repository Bot Mentions
            </CardTitle>
          </div>
          <CardDescription>
            Repositories can inherit the installation
            defaults or switch to a custom bot list. Custom
            mode with no selected bots disables mentions for
            that repository.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sortedRepos.length > 0 ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {defaultBotHandlesSummary.length > 0
                    ? `Installation defaults: ${defaultBotHandlesSummary.map((handle) => `@${handle}`).join(", ")}`
                    : "Installation defaults: no bot mentions"}
                </span>
                {canManage && (
                  <span className="text-muted-foreground">
                    {hasPendingRepoBotSelection
                      ? "Saving repository bot mentions..."
                      : "Changes save automatically"}
                  </span>
                )}
              </div>

              <div className="max-h-130 space-y-3 overflow-y-auto rounded-lg border p-3">
                {repoBotSelectionEntries.map(
                  ({ repo, selection }) => {
                    const selectedBotHandles =
                      selection.selectedBotIds
                        .map((botId) =>
                          issueBotHandleById.get(botId)
                        )
                        .filter(
                          (handle): handle is string =>
                            Boolean(handle)
                        );
                    const assigneeSummary =
                      selection.assignees.length > 0
                        ? selection.assignees.join(", ")
                        : "No assignees configured";
                    const isPending =
                      isRepoBotSelectionPending(
                        repo.fullName
                      );

                    return (
                      <div
                        key={repo.id}
                        className="space-y-3 rounded-lg border px-4 py-3"
                      >
                        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                          <div className="min-w-0 flex-1 space-y-2">
                            <div className="flex items-center gap-2">
                              <span className="truncate text-sm font-medium">
                                {repo.fullName}
                              </span>
                              {repo.private && (
                                <Lock className="h-3 w-3 flex-shrink-0 text-muted-foreground" />
                              )}
                              <Badge variant="secondary">
                                {selection.botSelectionMode ===
                                "inherit"
                                  ? "Inherit"
                                  : "Custom"}
                              </Badge>
                              {isPending && (
                                <Badge variant="secondary">
                                  Saving...
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">
                              Assignees: {assigneeSummary}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {selection.botSelectionMode ===
                              "inherit"
                                ? defaultBotHandlesSummary.length >
                                  0
                                  ? `Uses installation defaults: ${defaultBotHandlesSummary.map((handle) => `@${handle}`).join(", ")}`
                                  : "Uses installation defaults with no bot mentions"
                                : selectedBotHandles.length >
                                    0
                                  ? `Custom mentions: ${selectedBotHandles.map((handle) => `@${handle}`).join(", ")}`
                                  : "Custom mode with no selected bots disables mentions for this repository"}
                            </p>
                          </div>

                          <div className="w-full lg:w-80 xl:w-96">
                            <Label
                              htmlFor={`repo-bot-mode-${repo.id}`}
                              className="sr-only"
                            >
                              Bot selection mode
                            </Label>
                            <Select
                              items={
                                BOT_SELECTION_MODE_OPTIONS
                              }
                              value={
                                selection.botSelectionMode
                              }
                              onValueChange={(value) => {
                                const nextSelection = {
                                  ...selection,
                                  botSelectionMode:
                                    value as RepoIssueBotSelectionMode,
                                } satisfies RepoIssueBotSelectionDraft;

                                void handleSaveRepoBotSelection(
                                  {
                                    repositoryFullName:
                                      repo.fullName,
                                    nextSelection,
                                    previousSelection:
                                      selection,
                                  }
                                );
                              }}
                              disabled={
                                !canManage ||
                                hasPendingRepoBotSelection
                              }
                            >
                              <SelectTrigger
                                id={`repo-bot-mode-${repo.id}`}
                                className="w-full min-w-0"
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="inherit">
                                  Inherit installation
                                  defaults
                                </SelectItem>
                                <SelectItem value="custom">
                                  Custom repository
                                  selection
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        {selection.botSelectionMode ===
                          "custom" && (
                          <div className="space-y-2">
                            {catalogIssueBots.length > 0 ? (
                              <>
                                <p className="text-xs text-muted-foreground">
                                  Select one or more catalog
                                  bots. Leave every bot
                                  unchecked to disable
                                  mentions for this
                                  repository.
                                </p>
                                <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                                  {catalogIssueBots.map(
                                    (issueBot) => (
                                      <div
                                        key={`${repo.id}-${issueBot.id}`}
                                        className="flex items-center gap-3 rounded-lg border px-3 py-2"
                                      >
                                        <Checkbox
                                          checked={selection.selectedBotIds.includes(
                                            issueBot.id
                                          )}
                                          onCheckedChange={() => {
                                            const nextSelection =
                                              {
                                                ...selection,
                                                selectedBotIds:
                                                  toggleCatalogBotSelection(
                                                    selection.selectedBotIds,
                                                    issueBot.id,
                                                    catalogIssueBots
                                                  ),
                                              } satisfies RepoIssueBotSelectionDraft;

                                            void handleSaveRepoBotSelection(
                                              {
                                                repositoryFullName:
                                                  repo.fullName,
                                                nextSelection,
                                                previousSelection:
                                                  selection,
                                              }
                                            );
                                          }}
                                          disabled={
                                            !canManage ||
                                            hasPendingRepoBotSelection
                                          }
                                        />
                                        <span className="text-sm">
                                          @{issueBot.handle}
                                        </span>
                                      </div>
                                    )
                                  )}
                                </div>
                              </>
                            ) : (
                              <p className="rounded-lg border border-dashed px-4 py-3 text-sm text-muted-foreground">
                                Add a bot to the catalog
                                above to enable custom
                                repository selection.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  }
                )}
              </div>
            </div>
          ) : (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No repositories accessible. Configure
              repository access in GitHub App settings.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
